import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { 
  Users, UserPlus, Search, Plus, X, Save, 
  CheckCircle, AlertCircle, ArrowRight, UserCheck, 
  Filter, Edit, Trash2, UserCog, UserMinus
} from 'lucide-react';

const CreateAssign = () => {
  const { currentUser } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [draggedEmployee, setDraggedEmployee] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); 
  
  // New state for tab management
  const [activeTab, setActiveTab] = useState('online');
  const [offlineUsers, setOfflineUsers] = useState([]);
  
  // Static color options
  const colorOptions = [
      { id: 'blue', value: '#3B82F6' },     // vivid blue
      { id: 'green', value: '#22C55E' },    // brighter green
      { id: 'yellow', value: '#EAB308' },   // rich yellow
      { id: 'red', value: '#DC2626' },      // deeper red
      { id: 'purple', value: '#7C3AED' },   // more vibrant purple
      { id: 'pink', value: '#F472B6' },     // lighter pink
      { id: 'indigo', value: '#4F46E5' },   // stronger contrast with blue/purple
      { id: 'teal', value: '#0D9488' }      // darker teal, clearer from green
  ];
  
  // Form data for creating/editing a group
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    color: '#3B82F6', // Default blue
    managers: [], // Changed to empty array instead of ['']
    members: []
  });
  
  // Live data states
  const [groups, setGroups] = useState([]);
  const [clockedInEmployees, setClockedInEmployees] = useState([]);
  
  // Fetch groups, clocked-in employees, and offline users
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        try {
          // Use the work-groups endpoint
          const response = await fetch(`${API_URL}/work-groups`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch groups: ${response.status} ${response.statusText}`);
          }
          
          const data = await response.json();
          setGroups(data);
        } catch (fetchErr) {
          console.error('Network error fetching groups:', fetchErr);
          setError(`Network error: ${fetchErr.message}`);
          setApiError(true);
          setGroups([]);
        }
      } catch (err) {
        setError('Failed to load groups. Please try again.');
        console.error('Error fetching groups:', err);
        setApiError(true);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchClockedInEmployees = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        try {
          const response = await fetch(`${API_URL}/labor/active`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch employees: ${response.status} ${response.statusText}`);
          }
          
          const data = await response.json();
          // The structure of the response might differ from what we expect
          const employeesList = data.usersList || data;
          setClockedInEmployees(Array.isArray(employeesList) ? employeesList : []);
        } catch (fetchErr) {
          console.error('Network error fetching employees:', fetchErr);
          setError(`Network error: ${fetchErr.message}`);
          setApiError(true);
          setClockedInEmployees([]);
        }
      } catch (err) {
        setError('Failed to load clocked-in employees. Please try again.');
        console.error('Error fetching clocked-in employees:', err);
        setApiError(true);
        setClockedInEmployees([]);
      } finally {
        setLoading(false);
      }
    };
    
    // New function to fetch offline users (not clocked in and not in groups)
    const fetchOfflineUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        try {
          // Get all users
          const response = await fetch(`${API_URL}/users`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
          }
          
          const allUsersData = await response.json();
          setAllUsers(allUsersData);
          
          // Get active labor entries to determine who's clocked in
          const activeResponse = await fetch(`${API_URL}/labor/active`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!activeResponse.ok) {
            throw new Error(`Failed to fetch active labor: ${activeResponse.status} ${activeResponse.statusText}`);
          }
          
          const activeData = await activeResponse.json();
          const activeEmployeesList = activeData.usersList || activeData;
          const activeEmployeeIds = Array.isArray(activeEmployeesList) 
            ? activeEmployeesList.map(e => e && e.employee_id).filter(Boolean)
            : [];
          
          // Get all assigned user IDs from groups
          const assignedEmployeeIds = [];
          groups.forEach(group => {
            if (Array.isArray(group.members)) {
              group.members.forEach(memberId => {
                assignedEmployeeIds.push(Number(memberId));
              });
            }
          });
          
          // Filter to get offline AND unassigned users
          const offlineUnassignedUsers = allUsersData.filter(user => 
            user.is_active &&                                 // Only active users
            !activeEmployeeIds.includes(Number(user.id)) &&  // Not clocked in
            !assignedEmployeeIds.includes(Number(user.id))   // Not in a group
          );
          
          setOfflineUsers(offlineUnassignedUsers);
        } catch (fetchErr) {
          console.error('Network error fetching offline users:', fetchErr);
          setError(`Network error: ${fetchErr.message}`);
          setApiError(true);
          setOfflineUsers([]);
        }
      } catch (err) {
        setError('Failed to load offline users. Please try again.');
        console.error('Error fetching offline users:', err);
        setApiError(true);
        setOfflineUsers([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Function to fetch managers (users with admin or manager roles)
    const fetchAvailableManagers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        try {
          const response = await fetch(`${API_URL}/users?role=manager,admin`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch managers: ${response.status} ${response.statusText}`);
          }
          
          const data = await response.json();
          setAvailableManagers(data);
        } catch (fetchErr) {
          console.error('Network error fetching managers:', fetchErr);
          setError(`Network error: ${fetchErr.message}`);
          setAvailableManagers([]);
        }
      } catch (err) {
        setError('Failed to load managers. Please try again.');
        console.error('Error fetching managers:', err);
        setAvailableManagers([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Execute the fetch functions
    fetchGroups();
    fetchClockedInEmployees();
    fetchAvailableManagers();
    fetchOfflineUsers(); // Added to fetch offline users
  }, [API_URL, groups.length]); // Add groups.length to dependency array to refresh when groups change

  useEffect(() => {
    if (groups && groups.length > 0) {
      const fixedGroups = groups.map(group => {
        let managersArray = [];
        
        // Handle all possible formats of managers data
        if (typeof group.managers === 'string') {
          try {
            // If it's a JSON string, parse it
            const parsed = JSON.parse(group.managers);
            managersArray = Array.isArray(parsed) ? parsed : 
                          (parsed ? [parsed] : []);
          } catch (e) {
            // This might be a string like "[3, 4]" that's failing to parse
            console.error(`Failed to parse managers JSON for group ${group.id}:`, e);
            
            // Try to handle string formatted as array
            if (group.managers.startsWith('[') && group.managers.endsWith(']')) {
              const cleanString = group.managers.substring(1, group.managers.length-1);
              const idStrings = cleanString.split(',').map(s => s.trim());
              managersArray = idStrings.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
            } else {
              managersArray = [];
            }
          }
        } else if (Array.isArray(group.managers)) {
          // If it's already an array, use it directly
          managersArray = group.managers;
        } else if (group.managers && typeof group.managers === 'object') {
          // Sometimes APIs return an object with numeric keys
          managersArray = Object.values(group.managers);
        } else if (group.managers && !isNaN(group.managers)) {
          // If it's a single numeric value, convert to array
          managersArray = [Number(group.managers)];
        }
        
        // Create a new group object with the parsed managers
        return {
          ...group,
          managers: managersArray
        };
      });
      
      // Only update state if the data has actually changed
      if (JSON.stringify(fixedGroups) !== JSON.stringify(groups)) {
        setGroups(fixedGroups);
      }
    }
  }, [groups]);

  // Filter employees based on search term and exclude those already in groups for online tab
  const filteredOnlineEmployees = React.useMemo(() => {
    if (!Array.isArray(clockedInEmployees)) {
      console.error('clockedInEmployees is not an array:', clockedInEmployees);
      return [];
    }
    
    // Get all employee IDs that are already assigned to groups
    const assignedEmployeeIds = [];
    groups.forEach(group => {
      if (Array.isArray(group.members)) {
        group.members.forEach(memberId => {
          assignedEmployeeIds.push(Number(memberId));
        });
      }
    });
    
    // Filter out employees that already have group assignments
    const unassignedEmployees = clockedInEmployees.filter(employee => {
      if (!employee) return false;
      return !assignedEmployeeIds.includes(Number(employee.employee_id));
    });
    
    // Then apply search term filtering
    if (!searchTerm) return unassignedEmployees;
    
    try {
      const searchLower = searchTerm.toLowerCase();
      return unassignedEmployees.filter(employee => {
        if (!employee) return false;
        
        return (
          (employee.full_name && employee.full_name.toLowerCase().includes(searchLower)) ||
          (employee.username && employee.username.toLowerCase().includes(searchLower)) ||
          (employee.business_unit && employee.business_unit.toLowerCase().includes(searchLower)) ||
          (employee.activity && employee.activity.toLowerCase().includes(searchLower))
        );
      });
    } catch (err) {
      console.error('Error filtering online employees:', err);
      return [];
    }
  }, [clockedInEmployees, searchTerm, groups]);

  // Filter offline users based on search term
  const filteredOfflineUsers = React.useMemo(() => {
    if (!Array.isArray(offlineUsers)) {
      console.error('offlineUsers is not an array:', offlineUsers);
      return [];
    }
    
    if (!searchTerm) return offlineUsers;
    
    try {
      const searchLower = searchTerm.toLowerCase();
      return offlineUsers.filter(user => {
        if (!user) return false;
        
        return (
          (user.full_name && user.full_name.toLowerCase().includes(searchLower)) ||
          (user.username && user.username.toLowerCase().includes(searchLower)) ||
          (user.email && user.email.toLowerCase().includes(searchLower))
        );
      });
    } catch (err) {
      console.error('Error filtering offline users:', err);
      return [];
    }
  }, [offlineUsers, searchTerm]);
  
  // Handle changing tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setActiveEmployees([]); // Reset selections when changing tabs
    setSearchTerm(''); // Reset search term
  };
  
  // Handle opening the create group modal
  const handleOpenCreateModal = () => {
    setGroupForm({
      name: '',
      description: '',
      color: '#3B82F6',
      managers: [], // Start with empty managers array
      members: []
    });
    setEditingGroup(null);
    setShowGroupModal(true);
  };
  
  const handleOpenEditModal = (group) => {
    console.log('Opening edit modal for group:', group);
    
    // Handle various formats of manager data
    let managerIds = [];
    
    // Parse managers if they're in a string format
    if (typeof group.managers === 'string') {
      try {
        const parsed = JSON.parse(group.managers);
        managerIds = Array.isArray(parsed) ? parsed : (parsed ? [parsed] : []);
      } catch (e) {
        console.error(`Failed to parse managers JSON in edit modal for group ${group.id}:`, e);
        
        // Try to handle string formatted as array
        if (group.managers.startsWith('[') && group.managers.endsWith(']')) {
          const cleanString = group.managers.substring(1, group.managers.length-1);
          const idStrings = cleanString.split(',').map(s => s.trim());
          managerIds = idStrings.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
        } else {
          managerIds = [];
        }
      }
    } else if (Array.isArray(group.managers)) {
      managerIds = group.managers;
    } else if (group.managers && typeof group.managers === 'object') {
      managerIds = Object.values(group.managers);
    } else if (group.managers && !isNaN(group.managers)) {
      managerIds = [Number(group.managers)];
    }
    
    // Convert all manager IDs to strings for consistent form handling
    const formattedManagerIds = managerIds
      .filter(id => id !== null && id !== undefined)
      .map(id => String(id));
    
    console.log('Formatted manager IDs:', formattedManagerIds);
    
    setGroupForm({
      name: group.name || '',
      description: group.description || '',
      color: group.color || '#3B82F6',
      managers: formattedManagerIds,
      members: group.members || []
    });
    
    setEditingGroup(group.id);
    setShowGroupModal(true);
  };
  
  // Handle group form submission
  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!groupForm.name) {
      setError('Group name is required');
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Format managers to ensure IDs are valid numbers
      const formattedManagers = groupForm.managers
        .filter(m => m.trim() !== '')
        .map(id => parseInt(id, 10));
      
      if (editingGroup) {
        // Update existing group
        const response = await fetch(`${API_URL}/work-groups/${editingGroup}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: groupForm.name,
            description: groupForm.description,
            color: groupForm.color,
            managers: formattedManagers
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to update group: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Refresh the groups list to show updated data
        const groupsResponse = await fetch(`${API_URL}/work-groups`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (groupsResponse.ok) {
          const groupsData = await groupsResponse.json();
          setGroups(groupsData);
        }
        
        setSuccess('Group updated successfully');
      } else {
        // Create new group
        const response = await fetch(`${API_URL}/work-groups`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: groupForm.name,
            description: groupForm.description,
            color: groupForm.color,
            managers: formattedManagers,
            is_active: true
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to create group: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Refresh the groups list to show the new group
        const groupsResponse = await fetch(`${API_URL}/work-groups`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (groupsResponse.ok) {
          const groupsData = await groupsResponse.json();
          setGroups(groupsData);
        }
        
        setSuccess('Group created successfully');
      }
      
      // Close modal and reset form
      setShowGroupModal(false);
      setEditingGroup(null);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error submitting group form:', err);
      setError(`An error occurred while saving the group: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle selecting a group for assignment
  const handleSelectGroup = (group) => {
    setSelectedGroup(group.id === selectedGroup ? null : group.id);
    setActiveEmployees([]);
  };
  
  // Handle toggling an employee for assignment (works for both online and offline)
  const handleToggleEmployee = (employee) => {
    const employeeId = activeTab === 'online' ? employee.employee_id : employee.id;
    
    if (!employee || !employeeId) {
      console.error('Invalid employee object:', employee);
      return;
    }
    
    setActiveEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };
  
  // Handle assigning selected employees to a group
  const handleAssignToGroup = async () => {
    if (!selectedGroup || activeEmployees.length === 0) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get the current group to preserve existing members
      const currentGroup = groups.find(g => g.id === selectedGroup);
      
      if (!currentGroup) {
        throw new Error('Selected group not found');
      }
      
      // Combine existing and new members, avoiding duplicates
      const existingMembers = Array.isArray(currentGroup.members) ? currentGroup.members : [];
      const allMembers = [...new Set([...existingMembers, ...activeEmployees])];
      
      // Call the API to assign users to the group
      const response = await fetch(`${API_URL}/work-groups/${selectedGroup}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          users: allMembers
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to assign users: ${response.status} ${response.statusText}`);
      }
      
      // Refresh the groups list to show updated data
      const groupsResponse = await fetch(`${API_URL}/work-groups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        setGroups(groupsData);
      }
      
      setSuccess(`${activeEmployees.length} employees assigned to group successfully`);
      setActiveEmployees([]);
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error assigning to group:', err);
      setError(`An error occurred while assigning employees: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle removing an employee from a group with labor record handling
  const handleRemoveFromGroup = async (groupId, employeeId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get the current group to update its members
      const currentGroup = groups.find(g => g.id === groupId);
      
      if (!currentGroup) {
        throw new Error('Group not found');
      }
      
      // Filter out the employee to remove
      const updatedMembers = Array.isArray(currentGroup.members) 
        ? currentGroup.members.filter(id => id !== employeeId)
        : [];
      
      // Check if employee is currently clocked in
      const checkActiveResponse = await fetch(`${API_URL}/labor/current/${employeeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (checkActiveResponse.ok) {
        const activeData = await checkActiveResponse.json();
        
        // If employee is clocked in, handle labor records
        if (activeData.active && activeData.entry) {
          // Step 1: Clock out the current entry
          await fetch(`${API_URL}/labor/${activeData.entry.id}/clockout`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              notes: `Auto clock-out due to group removal from ${currentGroup.name}`
            })
          });
          
          // Step 2: Create a new labor entry without the group
          await fetch(`${API_URL}/labor`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              employee_id: employeeId,
              full_name: activeData.entry.full_name,
              activity: activeData.entry.activity,
              business_unit: activeData.entry.business_unit,
              group: null, // No group assignment
              project: activeData.entry.project,
              shift: activeData.entry.shift,
              team: activeData.entry.team,
              location: activeData.entry.location,
              notes: `Auto clock-in after removal from group ${currentGroup.name}`
            })
          });
        }
      }
      
      // Call the API to update the group's users
      const response = await fetch(`${API_URL}/work-groups/${groupId}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          users: updatedMembers
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update group members: ${response.status} ${response.statusText}`);
      }
      
      // Refresh the groups list to show updated data
      const groupsResponse = await fetch(`${API_URL}/work-groups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        setGroups(groupsData);
      }
      
      setSuccess('Employee removed from group successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error removing from group:', err);
      setError(`An error occurred while removing the employee: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle deleting a group
  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/work-groups/${groupId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete group: ${response.status} ${response.statusText}`);
      }
      
      // Update local state
      setGroups(prevGroups => prevGroups.filter(group => group.id !== groupId));
      
      if (selectedGroup === groupId) {
        setSelectedGroup(null);
      }
      
      setSuccess('Group deleted successfully');
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error deleting group:', err);
      setError(`An error occurred while deleting the group: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Drag and drop handlers
  const handleDragStart = (e, employee) => {
    // For online employees use employee_id, for offline use id
    const employeeId = activeTab === 'online' ? employee.employee_id : employee.id;
    
    if (!employeeId) {
      console.error('Invalid employee for drag:', employee);
      return;
    }
    
    setDraggedEmployee(employeeId);
  };
  
  const handleDragOver = (e, groupId) => {
    e.preventDefault();
  };
  
  const handleDrop = async (e, groupId) => {
    e.preventDefault();
    
    try {
      if (!draggedEmployee) return;
      
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get the current group to preserve existing members
      const currentGroup = groups.find(g => g.id === groupId);
      
      if (!currentGroup) {
        throw new Error('Group not found');
      }
      
      // Combine existing members with the dragged employee, avoiding duplicates
      const existingMembers = Array.isArray(currentGroup.members) ? currentGroup.members : [];
      
      if (existingMembers.includes(draggedEmployee)) {
        setDraggedEmployee(null);
        return; // Employee is already a member of this group
      }
      
      const allMembers = [...existingMembers, draggedEmployee];
      
      // Call the API to update the group's users
      const response = await fetch(`${API_URL}/work-groups/${groupId}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          users: allMembers
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update group members: ${response.status} ${response.statusText}`);
      }
      
      // Refresh the groups list to show updated data
      const groupsResponse = await fetch(`${API_URL}/work-groups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        setGroups(groupsData);
      }
      
      // Brief success message
      setSuccess('Employee added to group');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Error handling drop:', err);
      setError(`An error occurred: ${err.message}`);
    } finally {
      setLoading(false);
      setDraggedEmployee(null);
    }
  };
  
  // API Error Alert Component
  const ApiErrorAlert = () => (
    <div className="bg-yellow-900/30 border border-yellow-800 text-yellow-300 px-4 py-3 rounded-md mb-4">
      <div className="flex items-start">
        <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="font-medium">API Connection Error</h4>
          <p className="text-sm mt-1">
            Unable to connect to the server. Using offline mode with mock data.
            Some features may be limited. Please check your network connection.
          </p>
        </div>
      </div>
    </div>
  );
  

  const getManagerNameById = (managerId) => {
    if (managerId === null || managerId === undefined) {
      return 'No Manager Assigned';
    }
    
    // Convert IDs to strings for consistent comparison
    const managerIdStr = String(managerId).trim();
    
    // First check in availableManagers list
    const manager = availableManagers.find(m => String(m.id).trim() === managerIdStr);
    if (manager) {
      return manager.full_name || manager.username || `Manager ${managerId}`;
    }
    
    // If not found, check in allUsers as fallback
    const userManager = allUsers.find(u => String(u.id).trim() === managerIdStr);
    if (userManager) {
      return userManager.full_name || userManager.username || `User ${managerId}`;
    }
    
    return `Unknown Manager (ID: ${managerId})`;
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-200">Create & Assign Groups</h2>
        
        <button 
          onClick={handleOpenCreateModal}
          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 
                   text-white rounded-md transition-colors text-sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Group
        </button>
      </div>
      
      {/* API Error Alert */}
      {apiError && <ApiErrorAlert />}
      
      {/* Error and Success Messages */}
      {error && (
        <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
          <button 
            onClick={() => setError(null)} 
            className="ml-auto text-red-300 hover:text-red-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-900/30 border border-green-800 text-green-300 px-4 py-3 rounded-md flex items-start">
          <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{success}</div>
          <button 
            onClick={() => setSuccess(null)} 
            className="ml-auto text-green-300 hover:text-green-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Groups Panel */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
          <div className="p-4 border-b border-slate-700 flex justify-between items-center">
            <h3 className="text-lg font-medium text-slate-200 flex items-center">
              <Users className="w-5 h-5 mr-2" /> 
              Groups
            </h3>
          </div>
          
          <div className="p-2">
            {loading && !apiError ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : !Array.isArray(groups) || groups.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No groups have been created yet.</p>
                <p className="text-sm mt-1">Create your first group to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {groups.map(group => (
                  <div 
                    key={group.id} 
                    className={`border rounded-md overflow-hidden ${selectedGroup === group.id ? 'border-blue-500' : 'border-slate-600'}`}
                    onDragOver={(e) => handleDragOver(e, group.id)}
                    onDrop={(e) => handleDrop(e, group.id)}
                  >
                    <div 
                      className="p-3 border-b border-slate-600 flex justify-between items-center cursor-pointer"
                      onClick={() => handleSelectGroup(group)}
                      style={{ backgroundColor: `${group.color}20` }}
                    >
                      <div className="flex items-center space-x-3">
                        {/* Colored circle indicator */}
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: group.color }}
                        ></div>
                        
                        <div>
                          <h4 className="font-medium text-slate-200">{group.name}</h4>
                          <div className="text-xs text-slate-400 flex items-center mt-1">
                            <UserCheck className="w-3 h-3 mr-1" />
                            <span>{Array.isArray(group.members) ? group.members.length : 0} members</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditModal(group);
                          }}
                          className="p-1 text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGroup(group.id);
                          }}
                          className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-700/50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {selectedGroup === group.id && (
                      <div className="p-3 bg-slate-700/30">
                        <div className="text-xs text-slate-400 mb-3">
                          Managers: {(group.managers && (
                            Array.isArray(group.managers) ? group.managers.length > 0 : 
                            typeof group.managers === 'string' && group.managers.includes('[')
                          )) ? (
                            (() => {
                              // Handle different formats of manager data
                              let managersList = [];
                              
                              if (typeof group.managers === 'string') {
                                // Try to parse as JSON first
                                try {
                                  const parsed = JSON.parse(group.managers);
                                  managersList = Array.isArray(parsed) ? parsed : [parsed];
                                } catch (e) {
                                  // If that fails, try manual parsing
                                  if (group.managers.startsWith('[') && group.managers.endsWith(']')) {
                                    const cleanString = group.managers.substring(1, group.managers.length-1);
                                    managersList = cleanString.split(',')
                                      .map(s => s.trim())
                                      .map(id => parseInt(id, 10))
                                      .filter(id => !isNaN(id));
                                  }
                                }
                              } else if (Array.isArray(group.managers)) {
                                managersList = group.managers;
                              }
                              
                              // Map IDs to names and render
                              return managersList.length > 0 ? 
                                managersList.map((id, index) => (
                                  <span key={`manager-${id}`}>
                                    {getManagerNameById(id)}
                                    {index < managersList.length - 1 ? ', ' : ''}
                                  </span>
                                )) : 'None assigned';
                            })()
                          ) : 'None assigned'}
                        </div>
                        
                        <h5 className="text-sm font-medium text-slate-300 mb-2">Group Members:</h5>
                        {Array.isArray(group.members) && group.members.length > 0 ? (
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {group.members.map(memberId => {
                              // Look for the member in allUsers
                              const member = allUsers.find(u => parseInt(u.id, 10) === parseInt(memberId, 10));
                              
                              return (
                                <div key={memberId} className="flex justify-between items-center bg-slate-700/50 rounded p-2">
                                  <div className="text-sm text-slate-300">
                                    {member ? (member.full_name || member.username) : `Employee ID: ${memberId}`}
                                  </div>
                                  <button
                                    onClick={() => handleRemoveFromGroup(group.id, memberId)}
                                    className="text-slate-400 hover:text-red-400"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-sm text-slate-500 italic">No members assigned</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Employees Panel with Tabs */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
          <div className="p-4 border-b border-slate-700">
            <h3 className="text-lg font-medium text-slate-200 flex items-center">
              <UserPlus className="w-5 h-5 mr-2" /> 
              Assign Employees
            </h3>
            
            {/* Tab Navigation */}
            <div className="flex border-b border-slate-700 mt-3">
              <button
                className={`flex items-center py-2 px-4 text-sm font-medium transition-colors ${
                  activeTab === 'online' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
                onClick={() => handleTabChange('online')}
              >
                <UserCog className="w-4 h-4 mr-2" />
                Online Employees
              </button>
              <button
                className={`flex items-center py-2 px-4 text-sm font-medium transition-colors ${
                  activeTab === 'offline' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
                onClick={() => handleTabChange('offline')}
              >
                <UserMinus className="w-4 h-4 mr-2" />
                Offline Employees
              </button>
            </div>
            
            <div className="mt-3 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder={`Search ${activeTab === 'online' ? 'clocked-in' : 'offline'} employees...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                         text-sm placeholder-slate-400
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="p-2">
            {loading && !apiError ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : activeTab === 'online' ? (
              // Online Employees Tab Content
              !Array.isArray(filteredOnlineEmployees) || filteredOnlineEmployees.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <UserCog className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No clocked-in employees found.</p>
                  {searchTerm && <p className="text-sm mt-1">Try adjusting your search criteria.</p>}
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-1 gap-2 max-h-[500px] overflow-y-auto p-2">
                    {filteredOnlineEmployees.map(employee => (
                      employee && (
                        <div 
                          key={employee.employee_id || `emp-${Math.random()}`}
                          className={`border ${
                            activeEmployees.includes(employee.employee_id) ? 'border-blue-500' : 'border-slate-600'
                          } rounded-md p-3 flex justify-between items-center cursor-pointer hover:bg-slate-700/30`}
                          onClick={() => handleToggleEmployee(employee)}
                          draggable
                          onDragStart={(e) => handleDragStart(e, employee)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center">
                              <div className={`w-4 h-4 rounded-full mr-3 border ${
                                activeEmployees.includes(employee.employee_id) 
                                  ? 'bg-blue-500 border-blue-500' 
                                  : 'bg-transparent border-slate-500'
                              }`}>
                                {activeEmployees.includes(employee.employee_id) && (
                                  <CheckCircle className="w-4 h-4 text-white" />
                                )}
                              </div>
                              <div>
                                <h4 className="font-medium text-slate-200">{employee.full_name || employee.username || 'Unknown Employee'}</h4>
                                <div className="flex flex-wrap text-xs text-slate-400 mt-1">
                                  <span className="mr-3">{employee.activity || 'No Activity'}</span>
                                  <span>{employee.business_unit || 'No Business Unit'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-slate-500">
                            {employee.clock_in_time ? new Date(employee.clock_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )
            ) : (
              // Offline Employees Tab Content
              !Array.isArray(filteredOfflineUsers) || filteredOfflineUsers.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <UserMinus className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No offline employees available to assign.</p>
                  {searchTerm && <p className="text-sm mt-1">Try adjusting your search criteria.</p>}
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-1 gap-2 max-h-[500px] overflow-y-auto p-2">
                    {filteredOfflineUsers.map(user => (
                      <div 
                        key={user.id || `user-${Math.random()}`}
                        className={`border ${
                          activeEmployees.includes(user.id) ? 'border-blue-500' : 'border-slate-600'
                        } rounded-md p-3 flex justify-between items-center cursor-pointer hover:bg-slate-700/30`}
                        onClick={() => handleToggleEmployee(user)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, user)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center">
                            <div className={`w-4 h-4 rounded-full mr-3 border ${
                              activeEmployees.includes(user.id) 
                                ? 'bg-blue-500 border-blue-500' 
                                : 'bg-transparent border-slate-500'
                            }`}>
                              {activeEmployees.includes(user.id) && (
                                <CheckCircle className="w-4 h-4 text-white" />
                              )}
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-200">{user.full_name || user.username || 'Unknown User'}</h4>
                              <div className="flex flex-wrap text-xs text-slate-400 mt-1">
                                <span className="mr-3">{user.email || 'No Email'}</span>
                                <span>{user.role || 'No Role'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
            
            {/* Assignment Actions */}
            {activeEmployees.length > 0 && (
              <div className="p-3 mt-3 border-t border-slate-700 flex items-center justify-between">
                <div className="text-sm text-slate-300">
                  <span className="font-medium">{activeEmployees.length}</span> {activeTab === 'online' ? 'employee' : 'user'}(s) selected
                </div>
                
                <button
                  onClick={handleAssignToGroup}
                  disabled={!selectedGroup}
                  className={`flex items-center px-4 py-2 rounded-md text-sm ${
                    selectedGroup
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Assign to Selected Group
                </button>
              </div>
            )}
          </div>
          
          {/* Drag and Drop Hint */}
          <div className="p-3 border-t border-slate-700 text-xs text-slate-400 flex items-center justify-center">
            <span>Tip: You can also drag and drop {activeTab === 'online' ? 'employees' : 'users'} directly into groups</span>
          </div>
        </div>
      </div>
      
      {/* Create/Edit Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg w-full max-w-md">
            <div className="p-4 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-slate-200">
                {editingGroup ? 'Edit Group' : 'Create New Group'}
              </h3>
              <button 
                onClick={() => setShowGroupModal(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleGroupSubmit} className="p-6">
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                  Group Name*
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({...groupForm, name: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="description" className="block text-sm font-medium text-slate-300 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={groupForm.description}
                  onChange={(e) => setGroupForm({...groupForm, description: e.target.value})}
                  rows="2"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                ></textarea>
              </div>
              
              {/* Manager Selection (Dropdown) */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-300">
                    Group Managers
                  </label>
                  <button
                    type="button"
                    onClick={() => setGroupForm({
                      ...groupForm,
                      managers: [...groupForm.managers, '']
                    })}
                    className="flex items-center text-xs text-blue-500 hover:text-blue-400"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Manager
                  </button>
                </div>
                
                {groupForm.managers.length === 0 ? (
                  <div className="mb-2">
                    <button
                      type="button"
                      onClick={() => setGroupForm({
                        ...groupForm,
                        managers: ['']
                      })}
                      className="w-full py-2 text-center border border-dashed border-slate-600 rounded-md text-slate-400 hover:text-slate-300 hover:border-slate-500"
                    >
                      Click to add a manager
                    </button>
                  </div>
                ) : (
                  groupForm.managers.map((managerId, index) => (
                    <div key={`manager-field-${index}`} className="flex items-center mb-2">
                      <select
                        value={managerId}
                        onChange={(e) => {
                          const updatedManagers = [...groupForm.managers];
                          updatedManagers[index] = e.target.value;
                          setGroupForm({...groupForm, managers: updatedManagers});
                        }}
                        className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select a manager</option>
                        {availableManagers.map(manager => (
                          <option 
                            key={manager.id} 
                            value={String(manager.id)}
                          >
                            {manager.full_name || manager.username}
                          </option>
                        ))}
                      </select>
                      
                      {groupForm.managers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updatedManagers = groupForm.managers.filter((_, i) => i !== index);
                            setGroupForm({...groupForm, managers: updatedManagers});
                          }}
                          className="ml-2 p-1 text-slate-400 hover:text-red-400 hover:bg-slate-600/30 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Group Color
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {colorOptions.map(color => (
                    <div 
                      key={color.id}
                      onClick={() => setGroupForm({...groupForm, color: color.value})}
                      className={`h-8 rounded-md cursor-pointer transition-all ${
                        groupForm.color === color.value 
                          ? 'ring-2 ring-offset-2 ring-offset-slate-800 ring-white' 
                          : 'hover:opacity-80'
                      }`}
                      style={{ backgroundColor: color.value }}
                    ></div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowGroupModal(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 
                           rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                           rounded-md transition-colors flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingGroup ? 'Update Group' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateAssign;
